import { useCallback } from 'react'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { TableDataQa } from 'commons/types/enums'
import { Request } from 'commons/utils/request/request'
import { Layer, PageSectionPrimary, Table } from 'ui/components'
import { useConfirmation } from 'ui/components/Dialog/ConfirmationDialogV2'

import { handlerServerError } from '@orbit/errors'

import { useHRRContext } from '../../context'
import { HRR_DETAILS_KEY } from '../../query'
import { UploadFilePayload } from '../../types'
import { columns } from './columns'
import { UploadButton } from './components'

const uploadReportFile = ({ file, reportCustomId }: UploadFilePayload) => {
  const form = new FormData()
  form.append('file', file)
  form.append('reportCustomId', reportCustomId.toString())
  return Request.post(Request.urls.bank.reportFileUpload, form)
}

export const UploadedFiles = () => {
  const { data, isLoading, routeApi } = useHRRContext()
  const {
    uploadedFiles,
    uploadedFilesTotalCount,
    highRiskReviewId,
    form: { uploadedFilesPage, uploadedFilesSize }
  } = data
  const queryClient = useQueryClient()
  const navigate = routeApi.useNavigate()
  const confirmation = useConfirmation()

  const { mutate: uploadFile } = useMutation<void, Error, UploadFilePayload>({
    mutationFn: (opts) => uploadReportFile(opts),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [HRR_DETAILS_KEY] })
    },
    onError: (error) => handlerServerError(error)
  })

  const { mutate: deleteFile } = useMutation<void, Error, { id: number }>({
    mutationFn: (opts) => Request.delete(Request.urls.bank.reportFile, opts),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [HRR_DETAILS_KEY] })
    },
    onError: (error) => handlerServerError(error)
  })

  const onFilesUpload = useCallback(
    async (files: File[]) => {
      const filePromises: any[] = []
      files.forEach((file) => {
        filePromises.push(
          uploadFile({
            file,
            reportCustomId: highRiskReviewId
          })
        )
      })
      await Promise.all(filePromises)
    },
    [highRiskReviewId]
  )

  const handleUpdatePeriod = async (fileName: string, id: number) => {
    await confirmation.showDeleteConfirmation({
      message: `Are you sure you want to delete ${fileName} file?`,
      submitButtonText: 'Delete',
      confirmCallback: async () => deleteFile({ id })
    })
  }

  return (
    <PageSectionPrimary
      title="Uploaded Files"
      anchor="uploaded_files"
      face="outlined"
      actions={<UploadButton onUpload={onFilesUpload} />}
    >
      <Layer rounded shadowed stretch>
        <Table
          loading={isLoading}
          dataQa={TableDataQa.HRR_UPLOADED_FILES}
          columns={columns(handleUpdatePeriod)}
          dataSource={uploadedFiles.map((el) => ({
            item: el
          }))}
          pagination={{
            page: uploadedFilesPage,
            size: uploadedFilesSize,
            total: uploadedFilesTotalCount,
            onChange: (page, size) => {
              navigate({
                to: '.',
                search: (prev) => ({ ...prev, uploadedFilesPage: page, uploadedFilesSize: size }),
                resetScroll: false
              })
            }
          }}
        />
      </Layer>
    </PageSectionPrimary>
  )
}
